import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { MessageCircle, Mail, Building2, Phone } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WholesaleList({ token, lang = 'es' }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    axios.get(`${API}/admin/registrations/wholesale`, { headers: { 'X-Admin-Token': token } })
      .then((res) => setItems(res.data || []))
      .catch((err) => console.error('[Wholesale] load failed:', err?.message || err))
      .finally(() => setLoading(false));
  }, [token]);

  const buildWaLink = (phone, name) => {
    const digits = String(phone || '').replace(/\D/g, '');
    const normalized = digits.length === 10 ? `57${digits}` : digits;
    const first = (name || '').split(/\s+/)[0] || '';
    const text = encodeURIComponent(
      lang === 'es'
        ? `Hola ${first}! 💕 Soy del equipo de Jelly by Enkor 🍓 Vi que te registraste como mayorista en Expobelleza 2026 — me encantaría coordinar una llamada para mostrarte nuestro catálogo mayorista y los precios especiales. ¿Cuándo te queda bien? ✨`
        : `Hi ${first}! 💕 I'm from the Jelly by Enkor team 🍓 I saw you registered as a wholesale partner at Expobelleza 2026 — I'd love to set up a quick call to show you our wholesale catalog and special pricing. When works for you? ✨`,
    );
    return `https://wa.me/${normalized}?text=${text}`;
  };

  if (loading) return <p className="text-sm text-[#574A66]">{lang === 'es' ? 'Cargando…' : 'Loading…'}</p>;
  if (items.length === 0) {
    return (
      <p className="text-sm text-[#574A66] italic" data-testid="wholesale-empty">
        {lang === 'es'
          ? 'Todavía no hay leads de mayorista / distribución. Cuando alguien marque "wholesale" o "distribuidor" en el formulario, aparecerán aquí.'
          : 'No wholesale leads yet. They will appear here once someone selects "wholesale" in the form.'}
      </p>
    );
  }

  return (
    <div className="space-y-3" data-testid="wholesale-list">
      {items.map((r) => (
        <div
          key={r.id}
          className="rounded-2xl bg-gradient-to-br from-[#FFE4EC] to-[#FAEFC2]/30 border border-[#FF7597]/20 p-4"
        >
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-display text-lg font-black tracking-tighter text-[#1F1A24]">
                  {r.name || '—'}
                </p>
                <span className="text-[10px] tracking-widest uppercase font-bold px-2 py-0.5 rounded-full bg-[#FF2D9C] text-white">
                  🔥 HOT
                </span>
                {r.business_city && (
                  <span className="inline-flex items-center gap-1 text-[10px] text-[#574A66]">
                    <Building2 className="w-3 h-3" />
                    {r.business_city}
                  </span>
                )}
              </div>
              <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#574A66]">
                <span className="inline-flex items-center gap-1">
                  <Phone className="w-3 h-3" />
                  {r.phone || '—'}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Mail className="w-3 h-3" />
                  {r.email || '—'}
                </span>
                {r.city && <span>📍 {r.city}</span>}
                {r.favorite_product && <span>💄 {r.favorite_product}</span>}
              </div>
            </div>

            <div className="flex gap-2 shrink-0">
              {r.phone && (
                <a
                  href={buildWaLink(r.phone, r.name)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`wholesale-wa-${r.id}`}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#25D366] text-white text-xs font-bold tracking-widest uppercase hover:scale-105 transition-transform shadow-sm"
                >
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp
                </a>
              )}
              {r.email && (
                <a
                  href={`mailto:${r.email}?subject=${encodeURIComponent('Propuesta mayorista Jelly by Enkor')}`}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1F1A24] text-white text-xs font-bold tracking-widest uppercase hover:bg-[#A99BE0] transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  Email
                </a>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
